import { FACADE_PATTERNS, FACADES, HANDLE_CHOICES, HANDLES, INTERNALS } from "../catalog";
import type { FacadePatternType, FacadeType, HandleType, ItemType } from "../types";
import { EGGER_DECOR_GROUPS, type EggerDecorGroup } from "./eggerDecors";

/**
 * Прайс. Цен в коде нет: все значения задаёт администратор в кабинете,
 * сервер хранит их в базе и отдаёт конструктору. Здесь — только перечень
 * строк прайса (что можно оценить) с названиями и единицами.
 */
export type PriceValues = Readonly<Record<string, number>>;

export type PriceUnit = "₽/м²" | "₽/шт" | "₽/компл." | "₽/м" | "₽/мм" | "%";

export interface PriceLine {
  key: string;
  label: string;
  unit: PriceUnit;
  hint?: string;
}

export interface PriceSection {
  id: string;
  title: string;
  hint?: string;
  lines: PriceLine[];
}

const DECOR_GROUPS = Object.keys(EGGER_DECOR_GROUPS) as EggerDecorGroup[];

export const priceKeys = {
  board: (group: EggerDecorGroup) => `board.${group}`,
  back: (group: EggerDecorGroup) => `back.${group}`,
  /** Старый «глянец» при загрузке становится МДФ в эмали и считается так же. */
  facade: (facade: FacadeType) => `facade.${facade === "gloss" ? "satin" : facade}`,
  paintTwoSidedPercent: "facade.paintTwoSidedPercent",
  pattern: (pattern: FacadePatternType) => `pattern.${pattern}`,
  handle: (handle: HandleType) => `handle.${handle}`,
  handlePerMm: (handle: "bar" | "integrated") => `handle.${handle}PerMm`,
  /** Старая «LED-полка» при загрузке становится полкой с подсветкой. */
  item: (type: ItemType) => `item.${type === "led" ? "shelf" : type}`,
  /** Покраска ящика комода внутри в цвет фасадов (15.10), за ящик. */
  drawerPaint: "item.drawerPaint",
  glassShelf: "glass.shelf",
  glassHolders: "glass.holders",
  sideMirror: "mirror.side",
  hinge: "hardware.hinge",
  slidingSystem: "hardware.slidingSystem",
  /** Подъёмник Aventos для фасада вверх (09.10), комплект на фасад. */
  lift: (type: "HL" | "HS" | "HF" | "HK") => `hardware.lift${type}`,
  /** Механизм откидного фасада вниз, комплект на фасад. */
  flapDown: "hardware.flapDown",
  leg: "hardware.leg",
  endLeg: "hardware.endLeg",
  ledProfile: "led.profile",
  ledCorner: "led.corner",
  ledDriver: "led.driver"
} as const;

/** Все строки прайса по разделам — для кабинета и проверки на сервере. */
export const priceSections = (): PriceSection[] => [
  {
    id: "board",
    title: "Плита ЛДСП 16 мм",
    hint: "Корпус, полки и фасады из ЛДСП — по группе декора Egger",
    lines: DECOR_GROUPS.map((group) => ({ key: priceKeys.board(group), label: EGGER_DECOR_GROUPS[group], unit: "₽/м²" }))
  },
  {
    id: "back",
    title: "Задняя стенка 8–10 мм",
    hint: "Задники шкафов, стеллажей и торцевых полок — по группе декора задника",
    lines: DECOR_GROUPS.map((group) => ({ key: priceKeys.back(group), label: EGGER_DECOR_GROUPS[group], unit: "₽/м²" }))
  },
  {
    id: "facade",
    title: "Фасады",
    hint: "Фасад ЛДСП считается по цене плиты своего декора",
    lines: [
      ...(["satin", "glass", "mirror"] as const).map((facade): PriceLine => ({
        key: priceKeys.facade(facade), label: FACADES[facade].name, unit: "₽/м²"
      })),
      { key: priceKeys.paintTwoSidedPercent, label: "Покраска МДФ с двух сторон — наценка", unit: "%" }
    ]
  },
  {
    id: "pattern",
    title: "Фрезеровка МДФ",
    hint: "Доплата к фасаду МДФ в эмали",
    lines: (Object.keys(FACADE_PATTERNS) as FacadePatternType[])
      .filter((pattern) => pattern !== "plain")
      .map((pattern) => ({ key: priceKeys.pattern(pattern), label: FACADE_PATTERNS[pattern].name, unit: "₽/м²" }))
  },
  {
    id: "handle",
    title: "Ручки",
    lines: [
      ...HANDLE_CHOICES.map((handle): PriceLine => ({ key: priceKeys.handle(handle), label: HANDLES[handle].name, unit: "₽/шт" })),
      { key: priceKeys.handlePerMm("bar"), label: "Ручка: доплата за длину сверх 220 мм", unit: "₽/мм" },
      { key: priceKeys.handlePerMm("integrated"), label: "Интегрированная ручка: доплата за длину сверх 220 мм", unit: "₽/мм" }
    ]
  },
  {
    id: "item",
    title: "Наполнение",
    hint: "Ящик — короб с направляющими; он же в ячейках стеллажа, комода и под дверями шкафа",
    lines: [
      ...(Object.keys(INTERNALS) as ItemType[])
        .filter((type) => type !== "led")
        .map((type): PriceLine => ({ key: priceKeys.item(type), label: INTERNALS[type].name, unit: "₽/шт" })),
      { key: priceKeys.drawerPaint, label: "Покраска ящика комода внутри", unit: "₽/шт", hint: "Доплата к ящику" }
    ]
  },
  {
    id: "glass",
    title: "Стекло и зеркало",
    lines: [
      { key: priceKeys.glassShelf, label: "Полка стеклянная", unit: "₽/м²" },
      { key: priceKeys.glassHolders, label: "Полкодержатели под стекло (4 шт.)", unit: "₽/компл." },
      { key: priceKeys.sideMirror, label: "Зеркало на боковине", unit: "₽/м²" }
    ]
  },
  {
    id: "hardware",
    title: "Фурнитура",
    lines: [
      { key: priceKeys.hinge, label: "Петля", unit: "₽/шт" },
      { key: priceKeys.slidingSystem, label: "Раздвижная система на шкаф", unit: "₽/компл." },
      { key: priceKeys.lift("HL"), label: "Подъёмник Aventos HL (фасад параллельно вверх)", unit: "₽/компл.", hint: "На один откидной фасад; петли не нужны" },
      { key: priceKeys.lift("HK"), label: "Подъёмник Aventos HK top (фасад поворачивается вверх)", unit: "₽/компл.", hint: "На один откидной фасад; петли не нужны" },
      { key: priceKeys.lift("HS"), label: "Подъёмник Aventos HS (фасад вверх над корпусом)", unit: "₽/компл.", hint: "На один откидной фасад; петли не нужны" },
      { key: priceKeys.lift("HF"), label: "Подъёмник Aventos HF (складной фасад)", unit: "₽/компл.", hint: "На один складной фасад; петли между частями — отдельно" },
      { key: priceKeys.flapDown, label: "Механизм откидного фасада вниз", unit: "₽/компл.", hint: "На один фасад; петли — отдельно" },
      { key: priceKeys.leg, label: "Опора шкафа, углового, стеллажа", unit: "₽/шт" },
      { key: priceKeys.endLeg, label: "Опора торцевых полок", unit: "₽/шт" }
    ]
  },
  {
    id: "led",
    title: "Подсветка",
    lines: [
      { key: priceKeys.ledProfile, label: "Профиль с лентой", unit: "₽/м" },
      { key: priceKeys.ledCorner, label: "Угловой стык профиля", unit: "₽/шт" },
      { key: priceKeys.ledDriver, label: "Блок питания", unit: "₽/шт" }
    ]
  }
];

export const priceLines = () => priceSections().flatMap((section) => section.lines);

/** Подпись строки прайса для сообщения «не задана цена: …». */
export const priceLineTitle = (key: string) => {
  for (const section of priceSections()) {
    const line = section.lines.find((candidate) => candidate.key === key);
    if (line) return `${section.title}: ${line.label}`;
  }
  return key;
};

export const MAX_PRICE_VALUE = 10_000_000;
export const MAX_PERCENT_VALUE = 1000;

/** Значение годится для строки прайса: число не меньше нуля в разумных пределах. */
export const validPriceValue = (line: Pick<PriceLine, "unit">, value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0 &&
  value <= (line.unit === "%" ? MAX_PERCENT_VALUE : MAX_PRICE_VALUE);

/**
 * Чтение прайса при расчёте. Незаданная цена считается нулём и попадает
 * в missing — тогда итог не показывается как окончательный.
 */
export const createPriceReader = (values: PriceValues | null | undefined) => {
  const missing = new Set<string>();
  return {
    get(key: string) {
      const value = values?.[key];
      if (typeof value !== "number" || !Number.isFinite(value)) {
        missing.add(key);
        return 0;
      }
      return value;
    },
    missing
  };
};

export type PriceReader = ReturnType<typeof createPriceReader>;

/** Цена строки, если она задана; иначе undefined — для подписей в меню. */
export const priceValue = (values: PriceValues | null | undefined, key: string) => {
  const value = values?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
};
